import type { AxiosInstance } from "axios";
import { useEffect, useState } from "react";

import { AppSidebar } from "../components/AppSidebar.js";
import { StorePicker } from "../components/StorePicker.js";
import { Badge } from "../components/ui/badge.js";
import { Button } from "../components/ui/button.js";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card.js";
import { Store } from "lucide-react";
import { SidebarInset, SidebarProvider } from "../components/ui/sidebar.js";
import { rememberedRange, rememberRange, type DateRange } from "../lib/dates.js";
import { NAV_SECTIONS, visibleNavSections } from "../lib/nav.js";
import { findReport } from "../lib/reports.js";
import { fetchOnboarding, isLive } from "../lib/onboarding.js";
import { fetchTrackingStart, formatTrackingStart } from "../lib/health.js";
import type { MemberRole } from "../lib/team.js";
import type { Session, SessionStore } from "../lib/session.js";
import { ExportsProvider } from "../lib/export-tray-store.js";
import { ExportTray } from "../components/ExportTray.js";
import { TrackingBanner } from "../components/TrackingBanner.js";
import { JoinRequestBanner } from "../components/JoinRequestBanner.js";
import type { StoreTracking } from "../lib/tracking.js";
import { HealthScreen } from "./HealthScreen.js";
import { MyExportsScreen } from "./MyExportsScreen.js";
import { OnboardingScreen } from "./OnboardingScreen.js";
import { AddStoreScreen } from "./AddStoreScreen.js";
import { TeamScreen } from "./TeamScreen.js";
import { BillingScreen } from "./BillingScreen.js";
import { IntegrationsScreen } from "./IntegrationsScreen.js";
import { ReportScreen } from "./ReportScreen.js";
import { UtmCheckerScreen } from "./UtmCheckerScreen.js";
import { DailyPulseScreen } from "./DailyPulseScreen.js";
import { CampaignCohortLtvScreen } from "./CampaignCohortLtvScreen.js";
import { CampaignStageAnalysisScreen } from "./CampaignStageAnalysisScreen.js";
import { LatentValueAnalysisScreen } from "./LatentValueAnalysisScreen.js";
import { AttributionModelAnalysisScreen } from "./AttributionModelAnalysisScreen.js";
import { CustomerJourneyOrdersScreen } from "./CustomerJourneyOrdersScreen.js";
import { LandingPageTrendScreen } from "./LandingPageTrendScreen.js";
import { NetworkUserGrowthScreen } from "./NetworkUserGrowthScreen.js";

/**
 * The signed-in frame: a pinned sidebar, a slim top bar, and the page.
 *
 * THE LAYOUT IS LEGACY'S — a white floating nav panel on a tinted page, a greeting above
 * the page title, the store picker top-right. A merchant moving between the two
 * dashboards should not have to relearn where anything is.
 *
 * THE STORE PICKER IS DRIVEN BY `/auth/me`, not by localStorage. A store revoked in the
 * backend disappears here on the next load; legacy cached the list and kept showing it.
 *
 * NAVIGATION IS `history.pushState`, NOT A PAGE LOAD. These are still real `<a href>`
 * elements — middle-click, "open in new tab" and Back all behave — but a plain anchor
 * would reload the bundle and re-run `/auth/me` on every click.
 */

function routeFromPath(pathname: string): string | null {
  const match = /^\/(reports\/[a-z-]+|utm-checker|daily-performance|daily-ad-performance|cohort-ltv|campaign-stage-analysis|latent-value-analysis|attribution-model-analysis|customer-journey-orders|network-user-growth|health|onboarding|settings\/(integrations|billing|add-store|team))$/.exec(pathname);
  return match?.[1] ?? null;
}

/** Legacy greets by time of day, in the BROWSER's clock — it is a greeting, not a metric. */
function greeting(at: Date = new Date()): string {
  const hour = at.getHours();
  if (hour < 12) return "Good Morning";
  if (hour < 17) return "Good Afternoon";
  return "Good Evening";
}

export function DashboardShell({
  client,
  session,
  selected,
  features,
  billingNotice,
  onSelectStore,
  onSignOut,
}: {
  readonly client: AxiosInstance;
  readonly session: Session;
  readonly selected: SessionStore | null;
  /** Enabled feature flags for this environment (from runtime config), gating nav items. */
  readonly features: readonly string[];
  readonly billingNotice: string | null;
  readonly onSelectStore: (store: SessionStore) => void;
  readonly onSignOut: () => Promise<void>;
}): React.JSX.Element {
  const [route, setRoute] = useState<string | null>(() => routeFromPath(window.location.pathname));

  /**
   * THE DATE RANGE LIVES HERE, not in the report screen.
   *
   * Each screen remounts when you switch reports — deliberately, so one report's rows can
   * never appear under another's headers — so a range held inside it reset every time.
   * Someone comparing last month across three reports had to re-pick the dates on each,
   * and the third page silently answered a different question from the first.
   */
  const [range, setRange] = useState<DateRange>(() => rememberedRange());

  const applyRange = (next: DateRange): void => {
    rememberRange(next);
    setRange(next);
  };

  /**
   * Whether the SELECTED store has finished setup. It drives ONE thing: hiding the Setup
   * nav item once the store is live (see visibleNavSections). We only hide when we KNOW it
   * is done — a failed or pending check leaves Setup visible rather than hiding it wrongly.
   * With no store selected there is nothing to set up, so the item is hidden.
   */
  const [setupComplete, setSetupComplete] = useState(false);
  useEffect(() => {
    if (selected === null) {
      setSetupComplete(true);
      return;
    }
    let cancelled = false;
    void fetchOnboarding(client)
      .then((status) => {
        if (!cancelled) setSetupComplete(isLive(status));
      })
      .catch(() => {
        if (!cancelled) setSetupComplete(false);
      });
    return () => {
      cancelled = true;
    };
  }, [client, selected]);

  const [trackingInfo, setTrackingInfo] = useState<{ formattedDate: string; days: number } | null>(null);

  useEffect(() => {
    // Cleared at once on a store change: until the new store's answer arrives the badge must show nothing, not the last store's date.
    setTrackingInfo(null);
    if (selected === null) return;
    let cancelled = false;
    void fetchTrackingStart(client)
      .then((data) => {
        if (!cancelled) setTrackingInfo(formatTrackingStart(data));
      })
      .catch(() => {
        if (!cancelled) setTrackingInfo(null);
      });
    return () => {
      cancelled = true;
    };
  }, [client, selected]);


  // Only owner/admin are ever offered Team; an unknown role is treated as the least
  // privileged. The API enforces the same gate, so this is courtesy, not security.
  const role: MemberRole = selected?.role === "owner" || selected?.role === "admin" ? selected.role : "analyst";
  const sections = visibleNavSections(NAV_SECTIONS, { role, setupComplete, features: new Set(features) });

  // The Back button must work. Without this the URL changes and the screen does not,
  // which is the failure people describe as "the back button is broken".
  useEffect(() => {
    const onPop = (): void => setRoute(routeFromPath(window.location.pathname));
    window.addEventListener("popstate", onPop);
    return () => window.removeEventListener("popstate", onPop);
  }, []);

  const navigate = (to: string, search = ""): void => {
    window.history.pushState(null, "", `/${to}${search}`);
    setRoute(to);
  };

  /**
   * From a banner line to where it is fixed: the store is selected first (an agency's warning is
   * usually about a store other than the one open), then its Plugin tab — or Setup for a store
   * whose embed was never switched on, or Connect a store for one removed from Shopify.
   */
  const openTrackingWarning = (warning: StoreTracking): void => {
    if (warning.state === "uninstalled") {
      navigate("settings/add-store");
      return;
    }
    const store = session.stores.find((candidate) => candidate.tenantId === warning.storeId);
    if (store !== undefined && store.tenantId !== selected?.tenantId) onSelectStore(store);
    if (warning.state === "not_started") navigate("onboarding");
    else navigate("settings/integrations", "?tab=plugin");
  };

  return (
    <SidebarProvider>
      {/*
        The export tray's state lives HERE, above the keyed screens, so an export started
        on one report survives navigating to any other — the screens themselves remount
        on every switch by design.
      */}
      <ExportsProvider client={client} tenantId={selected?.tenantId ?? null}>
      <AppSidebar session={session} sections={sections} route={route} onNavigate={navigate} onSignOut={onSignOut} />

      <SidebarInset className="overflow-x-hidden">
        <header className="flex items-center gap-3 px-4 pt-5 pb-1 md:px-6">
          <p className="text-muted-foreground text-lg">{greeting()}</p>
          <div className="flex-1" />

          {trackingInfo !== null && (
            <div
              className="bg-card text-muted-foreground inline-flex h-9 items-center gap-2 rounded-md border px-3 text-xs sm:text-sm font-medium shadow-xs select-none"
              title={`Pixel Tracker recording data since ${trackingInfo.formattedDate} (${trackingInfo.days} ${trackingInfo.days === 1 ? "day" : "days"})`}
            >
              <span className="relative flex h-2 w-2 shrink-0">
                <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75" />
                <span className="relative inline-flex rounded-full h-2 w-2 bg-emerald-500" />
              </span>
              <span className="whitespace-nowrap">
                <span className="hidden md:inline text-muted-foreground">Tracking since </span>
                <span className="text-foreground font-medium">{trackingInfo.formattedDate}</span>{" "}
                <span className="text-muted-foreground font-normal">
                  ({trackingInfo.days} {trackingInfo.days === 1 ? "day" : "days"})
                </span>
              </span>
            </div>
          )}

          <StorePicker stores={session.stores} selected={selected} onSelect={onSelectStore} />


          {selected !== null && (
            <Badge variant="outline" className="bg-card hidden h-9 px-3 sm:inline-flex">
              {selected.role}
            </Badge>
          )}
        </header>

        {session.stores.length > 0 && <TrackingBanner client={client} onOpen={openTrackingWarning} />}
        {selected !== null && (
          <JoinRequestBanner client={client} tenantId={selected.tenantId} onAskAgain={() => navigate("settings/add-store")} />
        )}

        {/*
          The 402 banner. It is a BANNER and not a redirect — the whole reason
          SUBSCRIPTION_REQUIRED is 402 rather than 403 is so a merchant whose trial ends
          stays on the page they were reading, with an offer, instead of being bounced to
          a login screen.
        */}
        {billingNotice !== null && (
          <div
            role="status"
            className="mx-4 mt-3 flex items-center gap-2 rounded-lg border border-amber-300 bg-amber-50 px-4 py-2.5 text-sm text-amber-900 md:mx-6 dark:border-amber-900 dark:bg-amber-950/40 dark:text-amber-200"
          >
            <span>{billingNotice}</span>
            <a href="/billing" className="font-medium underline underline-offset-4">
              Choose a plan
            </a>
          </div>
        )}

        <div className="flex-1 px-4 pt-2 pb-8 md:px-6">
          <Body
            client={client}
            route={route}
            selected={selected}
            hasStores={session.stores.length > 0}
            onNavigate={navigate}
            range={range}
            onRangeChange={applyRange}
          />
        </div>
      </SidebarInset>

      <ExportTray onNavigate={navigate} />
      </ExportsProvider>
    </SidebarProvider>
  );
}

function Body({
  client,
  route,
  selected,
  hasStores,
  onNavigate,
  range,
  onRangeChange,
}: {
  readonly client: AxiosInstance;
  readonly route: string | null;
  readonly selected: SessionStore | null;
  readonly hasStores: boolean;
  readonly onNavigate: (to: string) => void;
  readonly range: DateRange;
  readonly onRangeChange: (range: DateRange) => void;
}): React.JSX.Element {
  if (!hasStores) {
    /*
     * FIRST RUN — a just-signed-up owner has an org but no store yet.
     *
     * The ONLY correct way to add the first store is the grant-carrying connect flow
     * (AddStoreScreen -> POST /api/v2/stores/connect): the API mints a signed grant so the
     * store joins THIS org. A cold Shopify App Store install carries no grant, so it would
     * provision a SECOND org and orphan the one they just created (install.routes.ts §grant).
     *
     * So the empty state must not be a dead end, and add-store must stay reachable with zero
     * stores — this branch runs BEFORE the route table below, so without this the "Connect a
     * store" nav item just re-showed the empty card and the first store could never be added.
     */
    if (route === "settings/add-store") {
      return <AddStoreScreen client={client} />;
    }
    return (
      <Card className="mx-auto max-w-lg">
        <CardHeader>
          <CardTitle>Connect your Shopify store</CardTitle>
        </CardHeader>
        <CardContent className="flex flex-col items-start gap-4 text-sm">
          <p className="text-muted-foreground">
            Your workspace is ready. Connect your Shopify store to start seeing data — you will be sent to Shopify to
            approve access, and the store joins this workspace.
          </p>
          <Button onClick={() => onNavigate("settings/add-store")}>
            <Store className="size-4" />
            Connect your Shopify store
          </Button>
          <p className="text-muted-foreground text-xs">
            Were you invited to an existing workspace? Ask its owner to add you, then sign in again.
          </p>
        </CardContent>
      </Card>
    );
  }

  if (selected === null) {
    return <p className="text-muted-foreground text-sm">Select a store to continue.</p>;
  }

  if (route === "onboarding") {
    return (
      <OnboardingScreen
        key={`onboarding:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        onDone={() => onNavigate("reports/source-attribution")}
      />
    );
  }

  if (route === "health") {
    return <HealthScreen key={`health:${selected.tenantId}`} client={client} tenantId={selected.tenantId} />;
  }

  if (route === "exports") {
    return <MyExportsScreen key={`exports:${selected.tenantId}`} client={client} tenantId={selected.tenantId} />;
  }

  if (route === "settings/integrations") {
    return (
      <IntegrationsScreen
        // The query is in the key so "View" on a banner line re-opens the Plugin tab even when
        // Integrations is already the page on screen.
        key={`integrations:${selected.tenantId}:${window.location.search}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
      />
    );
  }

  if (route === "settings/billing") {
    return <BillingScreen key={`billing:${selected.tenantId}`} client={client} tenantId={selected.tenantId} />;
  }

  if (route === "settings/add-store") {
    return <AddStoreScreen key={`add-store:${selected.tenantId}`} client={client} />;
  }

  if (route === "settings/team") {
    // Unknown roles read as the least privileged, as the shell's own role check does.
    const viewerRole: MemberRole = selected.role === "owner" || selected.role === "admin" ? selected.role : "analyst";
    return <TeamScreen key={`team:${selected.tenantId}`} client={client} viewerRole={viewerRole} />;
  }

  if (route === "utm-checker") {
    return (
      <UtmCheckerScreen
        key={`utm:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
      />
    );
  }

  if (route === "daily-performance" || route === "daily-ad-performance") {
    return (
      <DailyPulseScreen
        key={`pulse:${route}:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
        model={route === "daily-ad-performance" ? "lc" : "fc"}
      />
    );
  }

  if (route === "cohort-ltv") {
    return (
      <CampaignCohortLtvScreen
        key={`cohort-ltv:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
      />
    );
  }

  if (route === "campaign-stage-analysis") {
    return (
      <CampaignStageAnalysisScreen
        key={`campaign-stage-analysis:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
      />
    );
  }

  if (route === "latent-value-analysis") {
    return (
      <LatentValueAnalysisScreen
        key={`latent-value-analysis:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
      />
    );
  }

  if (route === "attribution-model-analysis") {
    return (
      <AttributionModelAnalysisScreen
        key={`attribution-model-analysis:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
      />
    );
  }

    if (route === "customer-journey-orders") {
    return (
      <CustomerJourneyOrdersScreen
        key={`customer-journey-orders:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        storeName={selected.displayName}
      />
    );
  }

  if (route === "network-user-growth") {
    return <NetworkUserGrowthScreen client={client} range={range} onRangeChange={onRangeChange} />;
  }

  if (route === "reports/landing-page-trend") {
    return (
      <LandingPageTrendScreen
        key={`landing-page-trend:${selected.tenantId}`}
        client={client}
        tenantId={selected.tenantId}
        range={range}
        onRangeChange={onRangeChange}
        storeName={selected.displayName}
      />
    );
  }

  const reportKey = route?.startsWith("reports/") === true ? route.slice("reports/".length) : null;
  const report = reportKey === null ? undefined : findReport(reportKey);

  if (report !== undefined) {
    /*
     * KEYED BY REPORT AND STORE, so switching either REMOUNTS rather than reuses. A
     * reused component would keep the previous report's rows on screen under the new
     * report's headers until the fetch returned — numbers under the wrong labels, which
     * a merchant has no way to detect.
     */
    return (
      <ReportScreen
        key={`${report.key}:${selected.tenantId}`}
        client={client}
        report={report}
        tenantId={selected.tenantId}
        range={range}
        onRangeChange={onRangeChange}
        storeName={selected.displayName}
      />
    );
  }

  return <Landing onNavigate={onNavigate} />;
}

/** Where a merchant lands before choosing anything. */
function Landing({ onNavigate }: { readonly onNavigate: (to: string) => void }): React.JSX.Element {
  return (
    <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
      {NAV_SECTIONS.flatMap((section) =>
        (section.items ?? (section.to === undefined ? [] : [{ to: section.to, label: section.label, description: section.description ?? "" }])).map(
          (item) => ({ item, icon: section.icon }),
        ),
      ).map(({ item, icon: Icon }) => (
        <button
          key={item.to}
          type="button"
          onClick={() => onNavigate(item.to)}
          className="bg-card hover:border-primary/40 rounded-xl border p-5 text-left shadow-sm transition-colors"
        >
          <Icon className="text-primary mb-3 size-5" />
          <p className="font-medium">{item.label}</p>
          <p className="text-muted-foreground mt-1 text-sm">{item.description}</p>
        </button>
      ))}
    </div>
  );
}
